import React, {useState} from 'react';
import {Badge} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconArrowRight} from '@tabler/icons-react';
import type {
  SkillSignOffCriterionResultEnum,
  SkillSignOffResponse,
} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Spinner} from 'src/design/atoms/Spinner';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {useGetMySkillSignOffHistoryQuery} from 'src/redux/queries/skillSignOff';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const PAGE_SIZE = 5;

const CRITERION_RESULT_LABEL: Record<SkillSignOffCriterionResultEnum, string> =
  {
    0: 'Unknown',
    1: 'Pass',
    2: 'Fail',
    3: 'N/A',
  };

const paginate = <T,>(items: T[], page: number, pageSize: number): T[] =>
  items.slice((page - 1) * pageSize, page * pageSize);

/**
 * `/skill-sign-off/my-history` is self-scoped server-side to the current
 * user. Only actionable (not-yet-evaluated) requests are shown — evaluated
 * requests belong to history, not an action list. Rows always expand in
 * place on click; a caller with Training Update additionally sees an
 * Evaluate button in the expanded detail panel — the row itself never
 * navigates. The card renders nothing when there is nothing actionable.
 */
export const SkillRequestsCard: React.FC = () => {
  const navigate = useAppNavigate();
  const {currentPage: requestsPage, handlePageChange} = useListState({
    identifier: 'my-skill-sign-off-requests',
    pageSize: PAGE_SIZE,
  });
  const [expandedRequestId, setExpandedRequestId] = useState<
    number | undefined
  >();

  const trainingPerms = useModulePermissions(ModuleEnum.Training);

  const {
    data: history,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    error: historyError,
  } = useGetMySkillSignOffHistoryQuery({start: 0, length: 100});

  const actionableHistory = (history ?? [])
    .filter(item => item.result === undefined || item.result === null)
    .slice()
    .sort(
      (a, b) =>
        parseUtcDate(b.requestedAt).getTime() -
        parseUtcDate(a.requestedAt).getTime()
    );

  const handleRequestRowClick = (id: number | string) => {
    const numericId = Number(id);
    setExpandedRequestId(current =>
      current === numericId ? undefined : numericId
    );
  };

  const renderRequestAction = (item: SkillSignOffResponse) => {
    if (!trainingPerms.update) {
      return null;
    }

    return (
      <Button
        variant="subtle"
        size="compact-sm"
        rightSection={<IconArrowRight size={14} />}
        onClick={event => {
          event.stopPropagation();
          navigate(PathUtility.TrainingSignOffEvaluate(String(item.id)));
        }}>
        Evaluate
      </Button>
    );
  };

  if (!isHistoryLoading && !isHistoryError && actionableHistory.length === 0) {
    return null;
  }

  return (
    <SectionCard title="Sign-Off Requests">
      <Box px="sm" py="xs">
        {isHistoryError ? (
          <Alert color="red">{parseErrorMessage(historyError)}</Alert>
        ) : isHistoryLoading ? (
          <Center py="xl">
            <Spinner loadingText="Loading your requests..." />
          </Center>
        ) : (
          <DataList
            data={paginate(actionableHistory, requestsPage, PAGE_SIZE)}
            meta={undefined}
            currentPage={requestsPage}
            totalRecords={actionableHistory.length}
            recordsPerPage={PAGE_SIZE}
            isLoading={false}
            noDataText="No sign-off requests yet."
            onRowClick={handleRequestRowClick}
            onPageChange={handlePageChange}
            onGetPillMapping={() => [{text: 'Pending', color: 'gray'}]}
            onRowTextRender={(item: SkillSignOffResponse) => item.skillName}
            onRowSubTextRender={(item: SkillSignOffResponse) =>
              `Requested ${parseUtcDate(item.requestedAt).toLocaleDateString()}`
            }
            onRenderDetailRowNode={(item: SkillSignOffResponse) => {
              if (expandedRequestId !== item.id) {
                return null;
              }
              const action = renderRequestAction(item);
              return (
                <Stack gap="xs" py="xs">
                  {item.signedOffByName && (
                    <Text size="xs" c="dimmed">
                      Evaluated by {item.signedOffByName}
                      {item.signedOffAt
                        ? ` on ${parseUtcDate(
                            item.signedOffAt
                          ).toLocaleDateString()}`
                        : ''}
                    </Text>
                  )}
                  {item.comment && <Text size="sm">{item.comment}</Text>}
                  {item.criterionResults.length > 0 && (
                    <Stack gap={4} mt={4}>
                      {item.criterionResults.map(result => (
                        <Group key={result.criterionId} justify="space-between">
                          <Text size="xs">{result.criterionText}</Text>
                          <Badge size="xs" variant="outline">
                            {CRITERION_RESULT_LABEL[result.result]}
                          </Badge>
                        </Group>
                      ))}
                    </Stack>
                  )}
                  {action && <Box>{action}</Box>}
                </Stack>
              );
            }}
            hasSearchTerm={false}
            hasCreationLogic={false}
          />
        )}
      </Box>
    </SectionCard>
  );
};
